import * as React from 'react';
import AssignmentLateOutlined from '@material-ui/icons/AssignmentLateOutlined';
import { fetchContractorAutomationsForClient } from 'slices/contractorAutomations';
import { fetchContractorExecutions } from 'slices/contractorExecutions';
import { fetchContractorIncidentReportInfo } from 'slices/contractorIncidentReportInfo';
import { getExecutionDeepLink, resolveFormPath } from 'helpers/executionDeepLink';
import { isActionRequiredIncident } from 'helpers/incidentExecutions';
import { ToDoDescriptor } from '../types';
import { useAppDispatch, useTypedSelector } from '../../../../combineReducers';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type RouteParams = {
  organizationId: string;
};

export const useIncidentReportsToDo = (): ToDoDescriptor[] => {
  const dispatch = useAppDispatch();
  const { organizationId } = useParams<RouteParams>();
  const { t } = useTranslation('subscriber');
  const { hasAccess, automationId, clientOrganizationId, isFetching: isFetchingInfo } = useTypedSelector(
    state => state.contractorIncidentReportInfo
  );
  const { executions, isFetching: isFetchingExecutions } = useTypedSelector(state => state.contractorExecutions);
  const { byClientOrganizationId, isFetching: isFetchingAutomations } = useTypedSelector(
    state => state.contractorAutomations
  );

  React.useEffect(() => {
    if (organizationId) {
      dispatch(fetchContractorIncidentReportInfo(organizationId));
    }
  }, [organizationId, dispatch]);

  // incident-report-info supplies the client org + automation to scope the executions call (CPRO-17684 AC #7).
  React.useEffect(() => {
    if (organizationId && hasAccess && clientOrganizationId && automationId) {
      dispatch(fetchContractorExecutions({ contractorId: organizationId, clientOrganizationId, automationId }));
    }
  }, [organizationId, hasAccess, clientOrganizationId, automationId, dispatch]);

  // formPath lives on the automation, not the execution, so fetch that client's automations once.
  React.useEffect(() => {
    if (!organizationId || !hasAccess || !clientOrganizationId || byClientOrganizationId[clientOrganizationId]) {
      return;
    }
    dispatch(fetchContractorAutomationsForClient({ contractorId: organizationId, clientOrganizationId }));
  }, [organizationId, hasAccess, clientOrganizationId, byClientOrganizationId, dispatch]);

  const label = t('newDashboard.toDoWidget.reviewIncidentDetails', 'Review incident details');
  const isFetching = isFetchingInfo || isFetchingExecutions || isFetchingAutomations;

  const incidents = hasAccess
    ? executions.filter(isActionRequiredIncident).reduce<ToDoDescriptor[]>((descriptors, execution) => {
        const formPath = resolveFormPath(execution, byClientOrganizationId[execution.organizationId]);
        const href = formPath && getExecutionDeepLink(execution, formPath);
        if (!href) {
          return descriptors;
        }
        return [
          ...descriptors,
          {
            id: `incidentReport-${execution.id}`,
            variant: 'link',
            label,
            icon: <AssignmentLateOutlined />,
            href,
            isVisible: true,
            isFetching
          }
        ];
      }, [])
    : [];

  // Hidden head descriptor keeps this source contributing `isFetching` even when it has no rows.
  return [
    {
      id: 'incidentReports',
      variant: 'link',
      label,
      icon: <AssignmentLateOutlined />,
      isVisible: false,
      isFetching
    },
    ...incidents
  ];
};
